export interface StallContext {
    page: string; surface: string; build: string | null; engine: string | null;
    encounter_id: string | null; actor_id: string | null; label: string;
    memory: Array<number | null>;
}
export interface StallEvent {
    id: string; kind: 'slow'; reason: string; detail: string; page: string; surface: string;
    build: string | null; engine: string | null; encounter_id: string | null; actor_id: string | null;
    ability_id: null; client_ms: number;
}

/** Self-contained so the dedicated worker and offline tests execute exactly the same machine. */
export function createStallWatchdog(now: () => number, send: (event: StallEvent) => void, id: () => string) {
    const thresholds = [5000, 15_000, 60_000, 300_000];
    let context: StallContext | undefined, lastPing = 0, active = false, stalled = false;
    let next = 0, total = 0, perStall = 0;
    const emit = (end: boolean, elapsed: number): void => {
        if (!context || total >= 30 || perStall >= 6) return;
        const actions = [];
        // PHP's existing allowlist clears encounter_id without an actor. Keep a typed compact
        // identity record too, including empty encounters and GM pages with no controlled actor.
        if (context.encounter_id) actions.push({ lane: 'stall', schema: 'stall-encounter-hex', operation_id: 'op-1',
            outcome: 'unknown', reason: context.encounter_id.replace(/-/g, '') });
        // Fixed order, bytes for heap values, GiB for deviceMemory; '*' means API unavailable.
        // Reuse the existing diagnostic record shape; no PHP contract or table changes.
        if (!end && perStall === 0) actions.push({ lane: 'stall', schema: 'heap-used.total.limit.device-gib.cores', operation_id: 'op-2',
            outcome: 'unknown', reason: context.memory.map(value => value === null ? '*' : String(value)).join(':') });
        const event: StallEvent = { id: id(), kind: 'slow', reason: (end ? 'stall-end:' : 'stall:') + context.page,
            detail: JSON.stringify({ caller: context.label, actions }), page: context.page, surface: context.surface,
            build: context.build, engine: context.engine, encounter_id: context.actor_id ? context.encounter_id : null,
            actor_id: context.encounter_id ? context.actor_id : null, ability_id: null,
            client_ms: Math.min(4294967295, Math.max(0, Math.round(elapsed))) };
        total++; perStall++;
        try { send(event); } catch { /* No retries or dependency on diagnostics. */ }
    };
    const poll = (): void => {
        if (!active || !context) return;
        const elapsed = now() - lastPing;
        // Reserve one event for recovery. Delayed worker ticks coalesce missed thresholds.
        if (next < thresholds.length && elapsed >= thresholds[next]! && total < 29 && perStall < 5) {
            stalled = true;
            emit(false, elapsed);
            while (next < thresholds.length && elapsed >= thresholds[next]!) next++;
        }
    };
    return {
        poll,
        ping(value: StallContext, sentAt: number, visible = true): void {
            // The sender's monotonic timestamp matters: queued pings are not new evidence of life.
            if (context && sentAt < lastPing) return;
            if (context && now() - sentAt >= 5000) {
                context = value; lastPing = sentAt; active = visible;
                return;
            }
            if (stalled) emit(true, sentAt - lastPing);
            context = value; lastPing = Math.min(sentAt, now()); active = visible;
            stalled = false; next = 0; perStall = 0;
        },
    };
}

/** Runs in a dedicated realm; neither timer nor POST needs the page to become responsive. */
export function runStallWorker(create: typeof createStallWatchdog, endpoint: string): void {
    const worker = self as unknown as {
        onmessage: ((event: MessageEvent<{ context: StallContext; at: number; visible: boolean }>) => void) | null;
        postMessage(value: unknown): void;
    };
    const machine = create(() => performance.timeOrigin + performance.now(), event => {
        void fetch(endpoint, { method: 'POST', credentials: 'same-origin', keepalive: true,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(event) }).catch(() => {});
    }, () => crypto.randomUUID());
    worker.onmessage = ({ data }) => machine.ping(data.context, data.at, data.visible);
    setInterval(machine.poll, 250);
    worker.postMessage('ready');
}
